import React, { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { companyName, IAddSellerInput, OtpReason } from "muminsmart-utils";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormControl,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import PhoneInputField from "@/components/common/PhoneInputField";
import { Eye, EyeOff } from "lucide-react";
import ContactOtpVerification, {
  OtpVerificationState,
} from "../ContactOtpVerification";
import type { ICity, IState } from "country-state-city";
import { useCountryStateCity } from "@/lib/hooks/useCountryStateCity";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface BusinessDetailProps {
  onPhoneVerificationChange: React.Dispatch<
    React.SetStateAction<OtpVerificationState>
  >;
  onEmailVerificationChange: React.Dispatch<
    React.SetStateAction<OtpVerificationState>
  >;
}

const normalizePhoneNumber = (phoneNumber: string) => {
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  return digitsOnly ? `+${digitsOnly}` : "";
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();

function BusinessDetail({
  onPhoneVerificationChange,
  onEmailVerificationChange,
}: BusinessDetailProps) {
  const form = useFormContext<IAddSellerInput>();

  const geo = useCountryStateCity();
  const countries = useMemo(
    () => (geo ? geo.Country.getAllCountries() : []),
    [geo],
  );
  const [states, setStates] = React.useState<IState[]>([]);
  const [cities, setCities] = React.useState<ICity[]>([]);
  const [showPassword, setShowPassword] = React.useState(false);
  const postalCode = useWatch({
    control: form.control,
    name: "postalCode",
  });
  const watchedPhoneNumber = useWatch({
    control: form.control,
    name: "phoneNumber",
  });
  const watchedEmail = useWatch({
    control: form.control,
    name: "email",
  });

  const normalizedPhoneNumber = normalizePhoneNumber(watchedPhoneNumber || "");
  const normalizedEmail = normalizeEmail(watchedEmail || "");

  const normalizeLocationName = React.useCallback((value: string) => {
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  }, []);

  const syncLocationFields = React.useCallback(
    (countryName: string, stateName: string, cityName: string) => {
      const normalizedCountryName = normalizeLocationName(countryName);
      const matchedCountry = countries.find(
        (country) =>
          normalizeLocationName(country.name) === normalizedCountryName,
      );

      if (!matchedCountry || !geo) {
        return;
      }

      const nextStates = geo.State.getStatesOfCountry(matchedCountry.isoCode);
      setStates(nextStates);
      form.setValue("country", matchedCountry.name, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const normalizedStateName = normalizeLocationName(stateName);
      const matchedState = nextStates.find(
        (state) => normalizeLocationName(state.name) === normalizedStateName,
      );

      if (!matchedState) {
        form.setValue("state", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
        setCities([]);
        form.setValue("city", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
        return;
      }

      const nextCities = geo.City.getCitiesOfState(
        matchedState.countryCode,
        matchedState.isoCode,
      );
      setCities(nextCities);
      form.setValue("state", matchedState.name, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const normalizedCityName = normalizeLocationName(cityName);
      const matchedCity = nextCities.find((city) => {
        const normalizedName = normalizeLocationName(city.name);
        return (
          normalizedName === normalizedCityName ||
          normalizedName.includes(normalizedCityName) ||
          normalizedCityName.includes(normalizedName)
        );
      });

      form.setValue("city", matchedCity?.name ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [countries, form, geo, normalizeLocationName],
  );

  React.useEffect(() => {
    const sanitizedPostalCode = `${postalCode ?? ""}`.replace(/\D/g, "");
    if (sanitizedPostalCode.length !== 6) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(async () => {
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${sanitizedPostalCode}`,
          { signal: controller.signal },
        );
        const result = (await response.json()) as Array<{
          Status: string;
          PostOffice?: Array<{
            Country: string;
            State: string;
            District: string;
            Name: string;
          }>;
        }>;
        const location = result[0]?.PostOffice?.[0];

        if (!location) {
          return;
        }

        syncLocationFields(
          location.Country,
          location.State,
          location.District || location.Name,
        );
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Failed to resolve postal code", error);
        }
      }
    }, 350);

    return () => {
      controller.abort();
      globalThis.clearTimeout(timeoutId);
    };
  }, [postalCode, syncLocationFields]);

  return (
    <>
      {/* firm Name */}
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Business Name</FormLabel>
            <FormControl>
              <Input
                placeholder={companyName}
                className="rounded-full p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Phone Number */}
      <FormField
        control={form.control}
        name="phoneNumber"
        render={() => (
          <FormItem>
            <ContactOtpVerification
              label="Business WhatsApp Number"
              value={normalizedPhoneNumber}
              channel="mobile"
              reason={OtpReason.MOBILE_VERIFICATION}
              verifiedText="WhatsApp number verified"
              onValidateField={() => form.trigger("phoneNumber")}
              onVerifiedStateChange={onPhoneVerificationChange}
            >
              <FormControl>
                <Controller
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <PhoneInputField
                      country={"in"}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      inputClass={`!w-full !rounded-full !h-12 !pl-14 !pr-4 !text-base ${
                        form.formState.errors.phoneNumber
                          ? "!border-destructive"
                          : ""
                      }`}
                      buttonClass="!border-none !bg-transparent [&_.selected-flag]:!bg-transparent !pl-3"
                      containerClass="!w-full"
                      defaultErrorMessage="Invalid phone number"
                      dropdownClass="!rounded-md"
                    />
                  )}
                />
              </FormControl>
              <FormMessage />
            </ContactOtpVerification>
          </FormItem>
        )}
      />

      {/* Email */}
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <ContactOtpVerification
              label="Business Email"
              value={normalizedEmail}
              channel="email"
              reason={OtpReason.EMAIL_VERIFICATION}
              verifiedText="Email verified"
              onValidateField={() => form.trigger("email")}
              onVerifiedStateChange={onEmailVerificationChange}
            >
              <FormControl>
                <Input
                  placeholder="example@gmail.com"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </ContactOtpVerification>
          </FormItem>
        )}
      />

      {/* Password */}
      <FormField
        control={form.control}
        name="password"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Password</FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="********"
                  className="rounded-full p-5 border-muted bg-white pr-10"
                  {...field}
                  aria-invalid={
                    form.formState.errors.password ? "true" : "false"
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted"
                >
                  {showPassword ? <EyeOff size={30} /> : <Eye size={30} />}
                </button>
              </div>
            </FormControl>
            <FormMessage />
            <FormDescription>
              The password should not be same as your user.
            </FormDescription>
          </FormItem>
        )}
      />

      {/* Address Line 1 */}
      <FormField
        control={form.control}
        name="addressLine1"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Address Line 1</FormLabel>
            <FormControl>
              <Input
                placeholder="123 Main St"
                className="rounded-full p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Address Line 2 */}
      <FormField
        control={form.control}
        name="addressLine2"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Address Line 2</FormLabel>
            <FormControl>
              <Input
                placeholder="Apartment, suite, etc."
                className="rounded-full p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Postal Code */}
      <FormField
        control={form.control}
        name="postalCode"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Postal Code</FormLabel>
            <FormControl>
              <Input
                placeholder="452001"
                className="rounded-full p-5 border-muted bg-white"
                {...field}
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => {
                  field.onChange(event.target.value.replace(/\D/g, ""));
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Country */}
      <FormField
        control={form.control}
        name="country"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Country</FormLabel>
            <Select
              onValueChange={(value) => {
                const country = countries.find((c) => c.name === value);
                if (!country || !geo) return;

                form.setValue("country", country.name);

                const { isoCode } = country;
                const statesOfCountry = geo.State.getStatesOfCountry(isoCode);
                setStates(statesOfCountry);
                form.setValue("state", "");

                // Reset cities when country changes
                setCities([]);
                form.setValue("city", "");
              }}
              value={field.value}
            >
              <SelectTrigger className="w-full rounded-full p-5 border-muted bg-white text-[0.9rem]">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem value={country.name} key={country.isoCode}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* State */}
      <FormField
        control={form.control}
        name="state"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>State</FormLabel>
            <Select
              onValueChange={(value) => {
                const state = states.find((s) => s.name === value);
                if (!state || !geo) return;

                form.setValue("state", state.name);

                const { isoCode } = state;
                const citiesOfState = geo.City.getCitiesOfState(
                  state.countryCode,
                  isoCode,
                );
                setCities(citiesOfState);
                form.setValue("city", "");
              }}
              value={field.value}
            >
              <SelectTrigger className="w-full rounded-full p-5 border-muted bg-white text-[0.9rem]">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                {states.map((state) => (
                  <SelectItem value={state.name} key={state.isoCode}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* City */}
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>City</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className="w-full rounded-full p-5 border-muted bg-white text-[0.9rem]">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                {cities.map((city) => (
                  <SelectItem value={city.name} key={city.name}>
                    {city.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex items-center justify-center my-6">
        <div className="grow border-t border-[#EADDC3]" />
        <span className="px-4 text-gray-600 text-sm tracking-widest font-medium">
          SHIPPING DETAILS
        </span>
        <div className="grow border-t border-[#EADDC3]" />
      </div>

      {/* Shipping Charge */}
      <FormField
        control={form.control}
        name="shippingCharge"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">
              Shipping Charge per kg
            </FormLabel>
            <FormControl>
              <Input
                type="number"
                placeholder="50"
                className="rounded-full p-5 border-muted bg-white"
                {...field}
                onChange={(e) => {
                  field.onChange(Number(e.target.value));
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="transportCharge"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Transport Charge</FormLabel>
            <FormControl>
              <Input
                type="number"
                placeholder="40"
                className="rounded-full p-5 border-muted bg-white"
                {...field}
                onChange={(e) => {
                  field.onChange(Number(e.target.value));
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex items-center justify-center my-6">
        <div className="grow border-t border-[#EADDC3]" />
        <span className="px-4 text-gray-600 text-sm tracking-widest font-medium">
          OTHER DETAILS
        </span>
        <div className="grow border-t border-[#EADDC3]" />
      </div>

      {/* GST Number */}
      <FormField
        control={form.control}
        name="gstNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">GST No</FormLabel>
            <FormControl>
              <Input
                placeholder="55XXXXX5555X5XX"
                className="rounded-full p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Gumasta License */}
      <FormField
        control={form.control}
        name="gumastaLicense"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Gumasta License</FormLabel>
            <FormControl>
              <Input
                placeholder="123456"
                className="rounded-full p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* CIN Number */}
      <FormField
        control={form.control}
        name="cinNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">CIN Number</FormLabel>
            <FormControl>
              <Input
                placeholder="X55555XX5555XXX555555"
                className="rounded-full p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* PAN Number */}
      <FormField
        control={form.control}
        name="panNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Business PAN Number</FormLabel>
            <FormControl>
              <Input
                placeholder="XXXXX3333X"
                className="rounded-full p-5 border-muted bg-white"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Authorized Signatory */}
      <FormField
        control={form.control}
        name="authorizedSignatory"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-medium">Authorized Signatory</FormLabel>
            <FormControl>
              <FormControl>
                <Input
                  placeholder="Murtaza"
                  className="rounded-full p-5 border-muted bg-white"
                  {...field}
                />
              </FormControl>
            </FormControl>
            <FormDescription>
              This Authorized Signatory will be used on invoices raised on the
              seller&apos;s behalf.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

export default BusinessDetail;
